// Whole-month arithmetic over periods of time, and how much of a window they
// account for.
//
// Written for the driver application's employment history, but nothing here
// knows about employment: any record with a start and an optional end works —
// address history, licences held, time at a terminal. The domain decides what a
// gap means and whether it blocks anything; this file only finds them.
//
// A month index is `year * 12 + monthIndex`, so all arithmetic is integer and no
// date library is involved. Periods are inclusive at both ends: Mar 2020 to
// Mar 2020 is one month, not zero.

export const MONTHS_PER_YEAR = 12

/** A closed period. Both ends are inclusive month indices. */
export interface MonthPeriod {
  start: number
  end: number
}

/** An input period whose end may be missing, meaning it is still running. */
export interface OpenMonthPeriod {
  start: number
  end?: number | null
}

export interface MonthCoverage {
  /** Unaccounted periods inside the window, newest first. */
  gaps: MonthPeriod[]
  /** Months inside the window accounted for by at least one period. */
  coveredMonths: number
  /** Length of the window in months. */
  windowMonths: number
  /** The window itself, so callers can label its edges without recomputing. */
  window: MonthPeriod
}

/**
 * The month index of a date, or null if there isn't one.
 *
 * Read in UTC deliberately. These values come from month pickers and from
 * backend dates stored at UTC midnight; reading them locally moves a January
 * date into December for anyone west of UTC.
 */
export const toMonthIndex = (value?: Date | string | null): number | null => {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.getUTCFullYear() * MONTHS_PER_YEAR + date.getUTCMonth()
}

export const currentMonthIndex = (now: Date = new Date()): number =>
  now.getUTCFullYear() * MONTHS_PER_YEAR + now.getUTCMonth()

export const monthsInPeriod = (period: MonthPeriod): number =>
  Math.max(0, period.end - period.start + 1)

/**
 * `years` years of months ending with the current month, inclusive — ten years
 * is 120 months, the last of which is now.
 */
export const trailingMonthWindow = (
  years: number,
  now: Date = new Date()
): MonthPeriod => {
  const end = currentMonthIndex(now)
  return { start: end - years * MONTHS_PER_YEAR + 1, end }
}

/**
 * Collapse periods into the time they actually cover.
 *
 * Two rules nobody sees but everything depends on: overlapping periods must
 * never produce a gap between them, and periods that touch — one ending the
 * month before the next begins — are continuous.
 */
export const mergeMonthPeriods = (
  periods: OpenMonthPeriod[],
  openEnd: number = currentMonthIndex()
): MonthPeriod[] => {
  const closed = (periods ?? [])
    .map((period) => ({ start: period.start, end: period.end ?? openEnd }))
    .filter(
      (period) =>
        Number.isFinite(period.start) &&
        Number.isFinite(period.end) &&
        period.end >= period.start
    )
    .sort((a, b) => a.start - b.start)

  const merged: MonthPeriod[] = []
  for (const period of closed) {
    const last = merged[merged.length - 1]
    // `+ 1` is what makes touching periods continuous rather than a gap
    if (last && period.start <= last.end + 1) {
      last.end = Math.max(last.end, period.end)
    } else {
      merged.push({ ...period })
    }
  }
  return merged
}

const clipToWindow = (
  periods: MonthPeriod[],
  window: MonthPeriod
): MonthPeriod[] =>
  periods
    .map((period) => ({
      start: Math.max(period.start, window.start),
      end: Math.min(period.end, window.end),
    }))
    .filter((period) => period.end >= period.start)

/**
 * What a window is missing: the unaccounted periods inside it, newest first, and
 * how much of it is accounted for.
 *
 * A period with no end runs to the end of the window, so an ongoing job covers
 * up to today. Periods outside the window are ignored; periods straddling its
 * edge count only for the part inside.
 */
export const analyzeMonthCoverage = (
  periods: OpenMonthPeriod[],
  window: MonthPeriod
): MonthCoverage => {
  const covered = clipToWindow(mergeMonthPeriods(periods, window.end), window)

  const gaps: MonthPeriod[] = []
  let highest = window.end
  for (let i = covered.length - 1; i >= 0; i--) {
    if (covered[i].end < highest) {
      gaps.push({ start: covered[i].end + 1, end: highest })
    }
    highest = Math.min(highest, covered[i].start - 1)
  }
  if (highest >= window.start) {
    gaps.push({ start: window.start, end: highest })
  }

  return {
    gaps,
    coveredMonths: covered.reduce(
      (total, period) => total + monthsInPeriod(period),
      0
    ),
    windowMonths: monthsInPeriod(window),
    window,
  }
}

export const isFullyCovered = (
  periods: OpenMonthPeriod[],
  window: MonthPeriod
): boolean => analyzeMonthCoverage(periods, window).gaps.length === 0
